import { useState, useRef, useEffect } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6vfnvvb245v7h34ono0';

const serviceOptions = [
  'Rehabilitación Dental',
  'Limpieza y Prevención',
  'Estética Dental',
  'Prótesis y Coronas',
  'Ortodoncia',
  'Otro',
];

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function AppointmentModal({ isOpen, onClose }: AppointmentModalProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [messageLen, setMessageLen] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const formData = new FormData(e.currentTarget);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const handleClose = () => {
    setStatus('idle');
    formRef.current?.reset();
    setMessageLen(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[#0A2540]/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-[0_30px_80px_rgba(10,37,64,0.4)]">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A2540] to-[#0d3060] p-7 rounded-t-3xl">
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-[#3A86FF]/25 rounded-xl">
              <i className="ri-calendar-check-2-line text-[#3A86FF] text-xl" />
            </div>
            <div>
              <h2 className="text-white font-bold text-xl">Agendar Cita</h2>
              <p className="text-white/55 text-sm">MF Dental · Ciudad de México</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-7">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-emerald-500 text-4xl" />
              </div>
              <h3 className="text-[#0A2540] font-bold text-2xl mb-3">¡Cita registrada!</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Hemos recibido tu solicitud. Un especialista de MF Dental se pondrá en contacto contigo muy pronto para confirmar tu cita.
              </p>
              <button
                onClick={handleClose}
                className="bg-[#3A86FF] hover:bg-[#1a6aff] text-white px-8 py-3 rounded-full font-semibold whitespace-nowrap cursor-pointer transition-colors"
              >
                Perfecto, gracias
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Name */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Nombre completo <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Ej: María González"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Correo electrónico <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="correo@ejemplo.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Teléfono <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="55 1234 5678"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all"
                />
              </div>

              {/* Service */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Servicio de interés
                </label>
                <select
                  name="service"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all cursor-pointer appearance-none bg-white"
                >
                  <option value="">Selecciona un servicio</option>
                  {serviceOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Fecha preferida
                </label>
                <input
                  type="date"
                  name="date"
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all cursor-pointer"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-[#0A2540] font-medium text-sm mb-1.5">
                  Mensaje o notas adicionales
                </label>
                <textarea
                  name="message"
                  maxLength={500}
                  rows={3}
                  placeholder="Cuéntanos brevemente sobre tu caso o consulta..."
                  onChange={(e) => setMessageLen(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3A86FF] focus:ring-2 focus:ring-[#3A86FF]/15 transition-all resize-none"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">Máximo 500 caracteres</span>
                  <span className={`text-xs ${messageLen > 450 ? 'text-rose-400' : 'text-gray-400'}`}>
                    {messageLen}/500
                  </span>
                </div>
              </div>

              {/* Error message */}
              {status === 'error' && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-600 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line" />
                  </div>
                  Ocurrió un error. Inténtalo de nuevo o llámanos al 55 3474 8982.
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-[#3A86FF] hover:bg-[#1a6aff] disabled:opacity-60 text-white py-4 rounded-xl font-semibold text-base whitespace-nowrap cursor-pointer transition-all duration-200 flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin text-lg" />
                    </div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-send-plane-2-line text-lg" />
                    </div>
                    Solicitar Cita
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-400">
                También puedes llamarnos al{' '}
                <a href="tel:5534748982" className="text-[#3A86FF] font-medium cursor-pointer hover:underline">
                  55 3474 8982
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
